import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Alert from "@material-ui/lab/Alert";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import Link from "@material-ui/core/Link";

import SendIcon from "@material-ui/icons/Send";

import { CLogger, TextUtils, id } from "../../../utils";
import {
  DEFAULT_LARGE_SEND_RECIPIENT_COUNT,
  DEFAULT_QUEUE_TIME,
  DEFAULT_RETRY_LIMIT,
  SUPPORT_EMAIL,
} from "../../../constants";

import { useSnack } from "../../../hooks/snack";
import { useBoolean } from "../../../hooks/state";
import {
  useConcurrentEmails,
  useConcurrentTaskCount,
  useEmailRecipientCount,
} from "../../../hooks/data/email";
import { useApplicationSettings } from "../../../hooks/data/settings";
import { getSendEmail } from "../../../contexts/firebase/FirebaseContext";

import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import LoadingText from "../../../components/core/LoadingText";

import { Countdown } from "./Countdown";

import { useActionStyles } from "../styles";
import useStyles from "./styles";

export interface EditorSendActionProps {
  /**
   * Disable this action.
   */
  disabled?: boolean;
}

const log = CLogger.category("components.editorToolbar.EditorSendAction");

/**
 * This action sends the current email.
 */
export const EditorSendAction = ({ disabled }: EditorSendActionProps) => {
  const rootClasses = useActionStyles();
  const classes = useStyles();

  const { emailId } = useParams();
  const { pushErrorSnack, pushClosableSnack } = useSnack();

  const [show, open, close] = useBoolean();
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const [app, appLoading] = useApplicationSettings();
  const [concurrentEmails = [], concurrentEmailsLoading] = useConcurrentEmails();
  const [taskCount = 0, taskCountLoading, refetchTaskCount] =
    useConcurrentTaskCount();
  const [retryAttempts, setRetryAttempts] = useState(0);

  const [recipientCount, recipientCountLoading, refetchRecipientCount] =
    useEmailRecipientCount(emailId);

  const exceededEmailConcurrencyLimit = Boolean(
    typeof app?.emailConcurrencyLimit === "number" &&
    !concurrentEmailsLoading &&
    concurrentEmails.length >= app.emailConcurrencyLimit
  );

  const exceededTaskConcurrencyLimit = Boolean(
    typeof app?.taskConcurrencyLimit === "number" &&
    !taskCountLoading &&
    taskCount >= app.taskConcurrencyLimit
  );

  const blocked = exceededEmailConcurrencyLimit || exceededTaskConcurrencyLimit;

  const queueTime = app?.concurrencyLimitReachedQueueTime ?? DEFAULT_QUEUE_TIME;
  const retryLimit = app?.concurrencyLimitReachedRetryLimit ?? DEFAULT_RETRY_LIMIT;

  const supportSuggestion =
    (recipientCount ?? 0) >
    (app?.recipientsRequiredForSupportMessage ?? DEFAULT_LARGE_SEND_RECIPIENT_COUNT);

  // Reset error when dialog options
  useEffect(() => {
    if (show) {
      // Reset error when dialog opens
      setError("");
      refetchTaskCount();
      refetchRecipientCount();
      setRetryAttempts(0);
    }
  }, [show, refetchTaskCount, refetchRecipientCount]);

  /**
   * Request that the current email be sent via SendGrid.
   */
  const sendHandler = async () => {
    const sendEmail = getSendEmail();

    if (!emailId) return;
    if (!sendEmail) return;

    try {
      setSending(true);
      const { data } = await sendEmail({ emailId, idempotencyKey: id() });
      if (data.status === "success") {
        close();
        setSending(false);
        pushClosableSnack(`Email processed for delivery.`);
        log({
          level: "info",
          message: data.message,
        });
      } else {
        setSending(false);
        setError(data.message);
        log({
          level: "error",
          message: data.message,
        });
      }
    } catch (error: any) {
      close();
      setSending(false);
      pushErrorSnack(`Error while sending email. ${error.message}`);
      log({
        level: "error",
        message: `Error while sending email. ${error.message}`,
        data: error,
      });
    }
  };

  const onQueueRetry = async () => {
    await refetchTaskCount();
    if (blocked) setRetryAttempts((attempts) => attempts + 1);
  };

  return (
    <>
      <Tooltip title="Send email" placement="bottom">
        <div>
          <IconButton
            className={rootClasses.button}
            classes={{ disabled: rootClasses.disabled }}
            disabled={disabled}
            onClick={open}
          >
            <SendIcon />
          </IconButton>
        </div>
      </Tooltip>
      <ConfirmDialog
        open={show}
        onClose={close}
        title={blocked ? "Your request is queued" : "Send email?"}
        confirmLabel="SEND"
        onConfirm={sendHandler}
        loading={
          sending ||
          appLoading ||
          concurrentEmailsLoading ||
          taskCountLoading ||
          recipientCountLoading
        }
        disableConfirm={Boolean(error) || blocked}
      >
        {sending ? (
          <LoadingText type="linear">Preparing email...</LoadingText>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : blocked ? (
          <>
            <Typography variant="body1" paragraph>
              {TextUtils.pluralWithCount(
                taskCount,
                "other request is",
                "other requests are"
              )}{" "}
              currently being processed.
            </Typography>
            {retryAttempts > retryLimit ? (
              <Alert severity="warning" className={classes.alert}>
                We attempted to queue your request, but we're still processing other
                requests. Please try again a little later or reach out to support for
                assistance.
              </Alert>
            ) : (
              <>
                <Typography variant="body1" paragraph>
                  Your request will automatically retry shortly.
                </Typography>
                {retryAttempts > 0 && (
                  <Alert severity="info" className={classes.alert}>
                    Taking longer than usual. Retrying again.
                  </Alert>
                )}
                <Box display="flex" justifyContent="center" marginBottom={2}>
                  <Countdown seconds={queueTime} loop onFinish={onQueueRetry} />
                </Box>
              </>
            )}
            <Typography variant="body2" paragraph>
              If your email is time-sensitive, please reach out for support at{" "}
              <Link
                href={`mailto:${SUPPORT_EMAIL}`}
                style={{ whiteSpace: "nowrap" }}
              >
                {SUPPORT_EMAIL}
              </Link>
            </Typography>
          </>
        ) : (
          <>
            {supportSuggestion && (
              <Alert severity="info" className={classes.alert}>
                This email will be sent to a large number of recipients. Please reach
                out to{" "}
                <Link
                  href={`mailto:${SUPPORT_EMAIL}`}
                  style={{ whiteSpace: "nowrap" }}
                >
                  {SUPPORT_EMAIL}
                </Link>{" "}
                if you require additional monitoring support.
              </Alert>
            )}
            <Typography variant="body1" paragraph>
              You are requesting to send{" "}
              {TextUtils.pluralWithCount(recipientCount ?? 0, "email")}. This may
              take several minutes to complete.
            </Typography>
          </>
        )}
      </ConfirmDialog>
    </>
  );
};
